/**
 * FormActionsSlot — an additive seam for the shared DDL form bodies' footer
 * row (`*EditFormBody`'s right-aligned [Cancel] [Save …] pair).
 *
 * Mounted in a constraint popover or the Designer pane, that row is fine
 * exactly where the form draws it. Inside the Compact VQE popover it was not:
 * the row scrolled away with the form body, leaving the popover's own
 * [Edit as SQL] action half-hidden below the fold and TWO competing action
 * rows on one screen.
 *
 * The slot fixes that without forking any form. With NO provider above it —
 * every existing call site — it renders the same `<div>` the form always
 * rendered, class list included, so those surfaces are byte-identical. With a
 * provider (only the compact popover's shell installs one) the buttons are
 * portalled into that shell's single pinned action row instead. The children
 * stay in the React tree under the form, so form state, handlers, tooltips
 * and `data-testid`s are all untouched.
 *
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** The markup every call site rendered before this seam existed. */
const DEFAULT_ROW_CLASS = 'flex items-center justify-end gap-2';

type FormActionsSlotValue = { readonly target: HTMLElement | null };

const FormActionsSlotContext = createContext<FormActionsSlotValue | null>(null);

export interface FormActionsSlotProviderProps {
  /**
   * Row element the hosted forms portal their action buttons into. `null`
   * while the host's callback ref has not attached yet (one commit), during
   * which the slot renders nothing rather than flashing an in-place row.
   */
  readonly target: HTMLElement | null;
  readonly children: ReactNode;
}

export function FormActionsSlotProvider({ target, children }: FormActionsSlotProviderProps) {
  const value = useMemo<FormActionsSlotValue>(() => ({ target }), [target]);
  return (
    <FormActionsSlotContext.Provider value={value}>{children}</FormActionsSlotContext.Provider>
  );
}

export interface FormActionsSlotProps {
  readonly children: ReactNode;
  /** Class list for the in-place row. Ignored once a host claims the slot. */
  readonly className?: string;
}

export function FormActionsSlot({ children, className = DEFAULT_ROW_CLASS }: FormActionsSlotProps) {
  const slot = useContext(FormActionsSlotContext);
  if (!slot) return <div className={className}>{children}</div>;
  return slot.target ? createPortal(children, slot.target) : null;
}
